import axios, { type AxiosInstance, type AxiosResponse } from "axios";

import {
  // tempConfigs,
  type StepSubmissionHandlerRespBody,
  type StandardSurveyEngineAnalytics,
} from "@neuromotion-inc/mightier-survey-engine";

import type {
  V3User,
  V3UserInfo,
  V3UserUpdateFields,
  UserNotificationPreferences,
  UserNotificationPreferencesUpdateFields,
  //
  ApiV3PasswordRequestResetResponse,
  ApiV3PasswordResetCompleteBody,
  ApiV3PasswordResetCompleteResponse,
  //
  ApiV3PasswordResetValidateBody,
  ApiV3PasswordResetValidateResponse,
  // survey engine
  AsssembleSurveyConfigResponse,
  SurveyStepSubmissionHandlerReqBody,
  //
  SessionAuthLoginResponse,
} from "@typings/mightierApiTypes";

// TODO: migrate type imports to here !!
import type {
  Page,
  V3Player,
  V3PlayerFilters,
  //
  PlayerActivityResponse,
  PlayerActivityFilters,
} from "@neuromotion-inc/mightier-ts-sdk";

import type {
  AccountActivationQueryParam,
  AccountCreateLambdaResponse,
  AccountCreateLambdaPayload,
} from "src/services/accountActivation/accountActivationTypes";

import * as Sentry from "@sentry/react";

import { useAccountActivationAuthStore } from "src/services/accountActivation/authStore";

import { setUinfoCookieForTagManager } from "@config/gtm";

/**
 * BACKEND API USED FOR EVERYTHING ELSE
 */

class BackendApi {
  protected _axios: AxiosInstance;

  constructor() {
    this._axios = axios.create({
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  async initiatePasswordReset(
    email: string,
  ): Promise<AxiosResponse<ApiV3PasswordRequestResetResponse>> {
    return await this._axios.request({
      method: "POST",
      url: "/api/v3/auth/password/request_reset/",
      data: { email },
    });
  }

  async passwordResetSetPassword(
    data: ApiV3PasswordResetCompleteBody,
  ): Promise<AxiosResponse<ApiV3PasswordResetCompleteResponse>> {
    return await this._axios.request({
      method: "POST",
      url: "/api/v3/auth/password/confirm_reset/",
      data,
    });
  }

  async validatePasswordResetToken(
    data: ApiV3PasswordResetValidateBody,
  ): Promise<AxiosResponse<ApiV3PasswordResetValidateResponse>> {
    return await this._axios.request({
      method: "POST",
      url: "/api/v3/auth/password/validate_reset/",
      data,
    });
  }

  async assembleSurvey(
    surveyId: string,
    meta: string,
  ): Promise<AxiosResponse<AsssembleSurveyConfigResponse>> {
    const resp = (await this._axios.request({
      method: "POST",
      url: "/api/v2/survey_engine/assemble_survey/",
      data: { survey_id: surveyId, meta },
    })) as AxiosResponse<AsssembleSurveyConfigResponse>;

    // NOTE: this sometimes makes local dev easier (since backend doesn't need to be updated every time
    // you make a survey config change/install to this repo)
    // resp.data.survey_config = tempConfigs.introProfileV1;

    return resp;
  }

  async trackSurveyAnalytic(
    data: StandardSurveyEngineAnalytics,
  ): Promise<AxiosResponse<Record<string, unknown>>> {
    return await this._axios.request({
      method: "POST",
      url: "/api/v2/survey_engine/events/",
      data,
    });
  }

  async wakeUpSubmitSurveyStepHandler(): Promise<AxiosResponse<unknown>> {
    return await this._axios.request({
      method: "POST",
      url: "/api/v2/survey_engine/submit_step/",
      // step_submission_handler_fn.ts in etl-pipeline repo
      headers: { "X-Mightier-Warm-Up": "1" },
    });
  }

  async submitSurveyStep(
    data: SurveyStepSubmissionHandlerReqBody,
  ): Promise<AxiosResponse<StepSubmissionHandlerRespBody>> {
    return await this._axios.request({
      method: "POST",
      url: "/api/v2/survey_engine/submit_step/",
      data,
    });
  }

  async sessionAuthLogin(
    username: string,
    password: string,
  ): Promise<AxiosResponse<SessionAuthLoginResponse>> {
    return await this._axios.request({
      method: "POST",
      url: "/api/v2/auth/session/login/",
      data: { email: username, password },
    });
  }

  async sessionAuthLogout(): Promise<AxiosResponse<{}>> {
    return await this._axios.request({
      method: "POST",
      url: "/api/v2/auth/session/logout/",
    });
  }

  async getUserInfo(): Promise<AxiosResponse<V3UserInfo>> {
    const resp = (await this._axios.request({
      url: "/api/v3/user_info/",
    })) as AxiosResponse<V3UserInfo>;

    Sentry.setUser({ id: resp.data.id });

    try {
      const userId = resp.data.id.toString();
      const accountId = resp.data.account.id.toString();
      setUinfoCookieForTagManager({ userId, accountId });
    } catch (e) {
      Sentry.captureException(e);
    }

    return resp;
  }

  async patchUser(
    userId: number,
    data: V3UserUpdateFields,
  ): Promise<AxiosResponse<V3User>> {
    return await this._axios.request({
      method: "PATCH",
      url: `/api/v3/users/${userId}/`,
      data,
    });
  }

  async patchUserNotificationPreferences(
    userId: number,
    data: UserNotificationPreferencesUpdateFields,
  ): Promise<AxiosResponse<UserNotificationPreferences>> {
    return await this._axios.request({
      method: "PATCH",
      url: `/api/v3/users/${userId}/notification_preferences/`,
      data,
    });
  }

  async listPlayers(
    filters: V3PlayerFilters = {},
  ): Promise<AxiosResponse<Page<V3Player>>> {
    return await this._axios.request({
      method: "GET",
      url: `/api/v3/players/`,
      params: filters,
    });
  }

  async retrievePlayer(playerId: number): Promise<AxiosResponse<V3Player>> {
    return await this._axios.request({
      method: "GET",
      url: `/api/v3/players/${playerId}/`,
    });
  }

  async retrievePlayerActivity(
    playerId: number,
    filters?: PlayerActivityFilters,
  ): Promise<AxiosResponse<PlayerActivityResponse>> {
    return await this._axios.request({
      method: "GET",
      url: `/api/v2/player/${playerId}/activity/`,
      params: filters,
    });
  }
}

export const backendApi = new BackendApi();

//
//
/**
 * NOTE: unfortunately account activation relies on legacy "oauth2" implementation which
 * would make the BackendApi.
 *
 * Eventually we'll move away from oauth2 to either session auth or JWT -
 * if it's JWT auth, see the `gs-care-guide-frontend` repo for a kickass JWT auth
 * version of this class
 *
 */

export class AccountActivationBackendApi extends BackendApi {
  constructor() {
    super();

    // modify every request to include oauth2 auth header.
    // NOTE: certain endpoints could be included if we want
    this._axios.interceptors.request.use((config) => {
      const tk = useAccountActivationAuthStore.getState().oauth2Token;
      config.headers.setAuthorization(`Bearer ${tk}`);
      return config;
    });
  }

  /**
   * This endpoint calls a lambda function that will:
   *  - get or create ALL MODELS inside djangoReturnValue (see docs below)
   *  - fetch shopify order details (can be used to display order information, e.g. at /start page)
   *  - if user has not already set password (user.password_set_by_user == False), authenticate the user via oauth2
   *    and return the token so frontend can make requests to set password, etc.
   *
   * NOTE: if user has already set their password:
   *  - val of `auth` key will be undefined,
   *  - djangoReturnValue.order.user.is_password_set === true
   *    - `is_password_set` actually returns the value of `user.password_set_by_user`
   *      (a hack added to backend code by alex bc legacy frontend repo was not easy to change)
   */
  async shopifyOrderGetOrCreate(
    checkoutData: AccountActivationQueryParam,
  ): Promise<AxiosResponse<AccountCreateLambdaResponse>> {
    const formattedData: AccountCreateLambdaPayload =
      checkoutData.version === undefined
        ? {
            version: undefined,
            order_id: checkoutData.orderId,
            checkout_token: checkoutData.checkoutToken,
            query_param_payload: checkoutData,
            customer_id: checkoutData.customerId,
            store_slug: checkoutData.storeSlug,
          }
        : checkoutData;

    const resp = await this._axios.request<AccountCreateLambdaResponse>({
      method: "POST",
      url: "/api/v2/shopify/order_create/",
      data: formattedData,
    });

    // we are now authenticated via oauth2 :)
    if (resp.data.auth) {
      const oauth2Token = resp.data.auth.access_token;
      useAccountActivationAuthStore.setState({ oauth2Token });
    }

    return resp;
  }

  async setPasswordForLoggedInUser(
    newPassord: string,
  ): Promise<AxiosResponse<unknown>> {
    return await this._axios.request({
      method: "PUT",
      url: "/api/v2/user/update_password/",
      data: { password: newPassord },
    });
  }

  /**
   * TODO:  make this more scalable lol
   */
  async postEventToLegacyEventCollector(
    userId: number,
    eventName: "privacy_policy_accepted",
    attrs: Record<string, string | number>,
  ): Promise<AxiosResponse<unknown>> {
    return await axios.post("https://event.mightier.com/", {
      user_id: userId,
      event: eventName,
      iso_timestamp: new Date().toISOString(),
      ...attrs,
    });
  }
}

export const accountActivationBackendApi = new AccountActivationBackendApi();
